/**
 * Party mode - an easter egg, in the spirit of D&D Beyond's hidden party wizard.
 *
 * For a couple of named accounts, hovering the Publisher Lines nav item runs a rainbow
 * sweep through its text. Nobody else ever sees it, and it does nothing but look silly.
 *
 * Why it tags a DOM node instead of rendering styled text: the navbar comes from
 * @leap4/frontend, and its link type is `{ text: string; link: string | LinksType[] }` -
 * `text` is a string, and the library also uses that same string as the React key
 * (Navbar.js, renderLinks). So there is no supported way to hand it a styled element.
 * Tagging the rendered node keeps the library contract untouched, and if the library's
 * markup ever changes the query simply finds nothing and the navbar renders normally.
 * An easter egg should fail invisibly.
 */

/** Lowercase, because the comparison lowercases the logged-in address too. */
const PARTY_MODE_USERS = ['nadavt@adte.com', 'dror@adte.com'];

/**
 * The nav label to light up. Must match the `text` in App's navbarLinks exactly - that
 * string is the only handle the library gives us on the rendered node.
 */
const PARTY_MODE_LABEL = 'Publisher Lines';

/** Consumed by the keyframes in App.css. */
const PARTY_MODE_CLASS = 'party-mode-link';

export function isPartyModeUser(email?: string): boolean {
  if (!email) return false;
  return PARTY_MODE_USERS.includes(email.trim().toLowerCase());
}

/**
 * Who gets the greeting on the onboarding upload modal.
 *
 * Its own list rather than reusing PARTY_MODE_USERS, even though the two currently hold the
 * same two addresses: they are separate jokes, and adding a third person to party mode should
 * not silently sign them up for this as well. Same spirit otherwise - fails invisibly, and
 * nothing depends on it.
 */
const ONBOARDING_GREETING_USERS = ['nadavt@adte.com', 'dror@adte.com'];

export function isOnboardingGreetingUser(email?: string): boolean {
  if (!email) return false;
  return ONBOARDING_GREETING_USERS.includes(email.trim().toLowerCase());
}

/**
 * Adds (or removes) the party class on whichever nav control carries the label.
 *
 * Both the desktop buttons and the mobile menu items are covered. Removal matters as much
 * as addition: switching to a non-party account in the same session has to clear it.
 */
export function tagPartyModeNavLink(enabled: boolean): void {
  if (typeof document === 'undefined') return;

  const candidates = Array.from(
    document.querySelectorAll<HTMLElement>(`button, [role="menuitem"]`),
  );

  for (const el of candidates) {
    const isTarget = (el.textContent || '').trim() === PARTY_MODE_LABEL;
    if (isTarget && enabled) {
      el.classList.add(PARTY_MODE_CLASS);
    } else if (el.classList.contains(PARTY_MODE_CLASS)) {
      el.classList.remove(PARTY_MODE_CLASS);
    }
  }
}
